/*
 * bdm-cart-multiple.js
 * ---------------------------------------------------------------------------
 * Borie de Maurel — two rules on the checkout button (cart page + cart drawer):
 *
 *   1. The TOTAL number of bottles in the cart must be a multiple of 6 (which
 *      also enforces a 6-bottle minimum, since 1-5 is blocked). The rule is on
 *      the cart TOTAL (all cuvees combined), not per product.
 *   2. The customer must tick a box accepting the CGV (terms of sale) and
 *      certifying they are 18 or older (added 20/10/2026, required by CGV
 *      art. 5.1 and 11.1). The ticked state is remembered for the browser
 *      session (sessionStorage), so it survives cart re-renders.
 *
 * 02/11/2026: texts in French, English and Dutch, chosen from window.bdmLang;
 * the CGV link uses window.bdmCgvUrl (localized page URL). Both are set by
 * snippets/bdm-cart-multiple.liquid.
 *
 * How it works:
 *   - Reads the authoritative bottle count from Shopify's /cart.js (item_count
 *     = sum of all line quantities).
 *   - The checkout button is disabled unless both rules are met; a message
 *     tells the customer how many bottles to add when rule 1 fails.
 *   - The theme (Horizon / Vessel) updates the cart by morphing the DOM after
 *     each quantity change, which re-renders the checkout button and wipes our
 *     state, so we re-evaluate after every cart mutation via a MutationObserver.
 *   - A capture-phase click guard is a second line of defence in case the
 *     button is briefly re-enabled by a morph between our updates.
 *
 * Limitation: accelerated wallets (Shop Pay / PayPal express, the
 * additional-checkout-buttons) bypass this button. The only bulletproof block
 * for those is a server-side Shopify Cart Validation Function.
 */
(function () {
  'use strict';

  var STEP = 6;
  // The theme's checkout button (shared by the cart page and the cart drawer).
  var BTN_SELECTOR = '.cart__checkout-button, #checkout, button[name="checkout"]';
  var TERMS_KEY = 'bdm_cgv_ok';

  var LANG = String(window.bdmLang || 'fr').slice(0, 2).toLowerCase();
  var CGV_LINK = '<a href="' + (window.bdmCgvUrl || '/pages/cgv') + '" target="_blank" rel="noopener">';
  var TEXTS = {
    fr: {
      terms: 'J’accepte les ' + CGV_LINK + 'conditions générales de vente</a> et je certifie avoir 18 ans ou plus.',
      notice: function (n) {
        return 'Les vins se commandent par multiple de 6 bouteilles. Ajoutez '
          + n + ' bouteille' + (n > 1 ? 's' : '') + ' pour valider votre commande.';
      }
    },
    en: {
      terms: 'I accept the ' + CGV_LINK + 'terms of sale</a> and certify that I am 18 or over.',
      notice: function (n) {
        return 'Our wines are sold in multiples of 6 bottles. Add '
          + n + ' bottle' + (n > 1 ? 's' : '') + ' to complete your order.';
      }
    },
    nl: {
      terms: 'Ik ga akkoord met de ' + CGV_LINK + 'algemene verkoopvoorwaarden</a> en verklaar dat ik 18 jaar of ouder ben.',
      notice: function (n) {
        return 'Onze wijnen worden verkocht per veelvoud van 6 flessen. Voeg nog '
          + n + (n > 1 ? ' flessen' : ' fles') + ' toe om uw bestelling af te ronden.';
      }
    }
  };
  var T = TEXTS[LANG] || TEXTS.fr;
  var TERMS_HTML = T.terms;

  var lastCount = null;   // last known bottle count, for the click guard
  var observer = null;    // disconnected during our own DOM writes
  var termsAccepted = false;
  try { termsAccepted = sessionStorage.getItem(TERMS_KEY) === '1'; } catch (e) {}

  function noticeText(missing) {
    return T.notice(missing);
  }

  function containerFor(btn) {
    return btn.closest('.cart__ctas') || btn.parentNode;
  }

  // Insert / update / remove the multiple-of-6 message next to a checkout
  // button, idempotently (never touch the DOM if nothing actually changed).
  function syncNotice(btn, ok, missing) {
    var container = containerFor(btn);
    if (!container) return;
    var notice = container.querySelector('.bdm-multiple-notice');

    if (ok) {
      if (notice) notice.parentNode.removeChild(notice);
      return;
    }

    var text = noticeText(missing);
    if (!notice) {
      notice = document.createElement('p');
      notice.className = 'bdm-multiple-notice';
      notice.setAttribute('role', 'alert');
      notice.textContent = text;
      container.insertBefore(notice, btn);
    } else if (notice.textContent !== text) {
      notice.textContent = text;
    }
  }

  // Insert the CGV + age checkbox right above a checkout button (once), and
  // keep its ticked state in line with termsAccepted.
  function syncTerms(btn) {
    var container = containerFor(btn);
    if (!container) return;
    var box = container.querySelector('.bdm-terms');
    if (!box) {
      box = document.createElement('label');
      box.className = 'bdm-terms';
      var input = document.createElement('input');
      input.type = 'checkbox';
      input.className = 'bdm-terms__input';
      input.required = true;
      var span = document.createElement('span');
      span.className = 'bdm-terms__text';
      span.innerHTML = TERMS_HTML;
      box.appendChild(input);
      box.appendChild(span);
      container.insertBefore(box, btn);
    }
    var cb = box.querySelector('.bdm-terms__input');
    if (cb && cb.checked !== termsAccepted) cb.checked = termsAccepted;
    if (termsAccepted) box.classList.remove('bdm-terms--error');
  }

  // Apply both rules to every checkout button on the page.
  function applyState(count) {
    lastCount = count;

    var buttons = document.querySelectorAll(BTN_SELECTOR);
    if (!buttons.length) return;

    var remainder = count % STEP;
    var ok = remainder === 0;
    var missing = ok ? 0 : STEP - remainder;
    var allowed = ok && termsAccepted;

    // Suspend the observer so our own writes don't trigger another refresh.
    if (observer) observer.disconnect();

    buttons.forEach(function (btn) {
      // Empty cart: leave the theme's own disabled state untouched.
      if (count === 0) {
        syncNotice(btn, true, 0);
        btn.classList.remove('bdm-checkout-blocked');
        return;
      }
      syncTerms(btn);
      if (btn.disabled !== !allowed) btn.disabled = !allowed;
      btn.classList.toggle('bdm-checkout-blocked', !allowed);
      syncNotice(btn, ok, missing);
    });

    if (observer) reconnect();
  }

  function refresh() {
    fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) { applyState(cart.item_count || 0); })
      .catch(function () { /* network hiccup: leave things as-is */ });
  }

  // Debounced refresh, so a burst of morph mutations only fetches once.
  var timer = null;
  function scheduleRefresh() {
    clearTimeout(timer);
    timer = setTimeout(refresh, 120);
  }

  function reconnect() {
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    // Re-evaluate whenever the cart region is morphed/re-rendered. We only act
    // when a checkout button is present, to keep other pages cheap.
    observer = new MutationObserver(function () {
      if (document.querySelector(BTN_SELECTOR)) scheduleRefresh();
    });
    reconnect();

    // Ticking / unticking the CGV + age box.
    document.addEventListener('change', function (e) {
      var cb = e.target;
      if (!cb || !cb.classList || !cb.classList.contains('bdm-terms__input')) return;
      termsAccepted = cb.checked;
      try { sessionStorage.setItem(TERMS_KEY, termsAccepted ? '1' : '0'); } catch (err) {}
      applyState(lastCount == null ? 0 : lastCount);
    });

    // Second line of defence: block the click if a rule is not met, even if
    // a morph momentarily re-enabled the button.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest(BTN_SELECTOR) : null;
      if (!btn) return;
      if (lastCount != null && lastCount > 0 && (lastCount % STEP !== 0 || !termsAccepted)) {
        e.preventDefault();
        e.stopPropagation();
        if (!termsAccepted) {
          document.querySelectorAll('.bdm-terms').forEach(function (b) {
            b.classList.add('bdm-terms--error');
          });
        }
        scheduleRefresh();
      }
    }, true);

    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Back/forward cache restores can show a stale cart, re-check.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) scheduleRefresh();
  });
})();
